if (!customElements.get('virgo-product')) {
  customElements.define('virgo-product', class extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.form = this.querySelector('.vp-form');
      this.status = this.querySelector('[data-status]');
      this.buttons = [...this.querySelectorAll('[data-submit]')];
      this.classList.add('vp-enhanced');
      this.setupGallery(options);
      this.querySelectorAll('[data-view-src]').forEach(button => {
        button.addEventListener('click', () => {
          const image = this.querySelector('[data-product-view]');
          if (!image) return;
          image.src = button.dataset.viewSrc;
          image.alt = button.dataset.viewAlt;
          image.dataset.productView = button.dataset.viewKind;
          this.querySelectorAll('[data-view-src]').forEach(control => control.setAttribute('aria-pressed', String(control === button)));
        }, options);
      });
      this.querySelectorAll('[data-slider-step]').forEach(button => {
        button.addEventListener('click', () => {
          const track = this.querySelector('[data-benefit-track]');
          if (!track) return;
          const card = track.querySelector('.vp-benefit');
          track.scrollBy({ left: (card.getBoundingClientRect().width + 16) * Number(button.dataset.sliderStep), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }, options);
      });
      this.motionPreference = matchMedia('(prefers-reduced-motion: reduce)');
      this.setupMotion();
      this.motionPreference.addEventListener('change', () => this.setupMotion(), options);
      this.form?.addEventListener('submit', event => this.addToCart(event), options);
      this.querySelector('[data-variant-select]')?.addEventListener('change', event => {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', event.target.value);
        url.hash = this.querySelector('.vp-hero-copy').id;
        window.location.assign(url.href);
      }, options);
      this.addEventListener('click', event => {
        const link = event.target.closest('a[href^="#"]');
        if (!link) return;
        const destination = document.getElementById(link.hash.slice(1));
        if (!destination || !this.contains(destination)) return;
        event.preventDefault();
        destination.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        destination.setAttribute('tabindex', '-1');
        destination.focus({ preventScroll: true });
      }, options);
      window.addEventListener('pageshow', () => this.resetButtons(), options);
    }
    disconnectedCallback() {
      if (this.gallery?.open) this.gallery.close();
      this.restoreGalleryScroll();
      cancelAnimationFrame(this.motionFrame);
      this.motionController?.abort();
      this.revealObserver?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
    }
    setupGallery(options) {
      this.gallery = this.querySelector('[data-gallery]');
      if (!this.gallery || typeof this.gallery.showModal !== 'function') return;
      this.galleryItems = [...this.querySelectorAll('[data-gallery-item]')];
      this.galleryIndex = 0;
      this.querySelectorAll('[data-gallery-open]').forEach(opener => {
        opener.addEventListener('click', event => {
          event.preventDefault();
          this.galleryOpener = opener;
          const kind = this.querySelector('[data-product-view]').dataset.productView;
          this.setGalleryImage(kind === 'base' ? 1 : kind === 'box' ? 2 : 0);
          this.gallery.showModal();
          this.galleryOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
        }, options);
      });
      this.querySelector('[data-gallery-close]').addEventListener('click', () => this.gallery.close(), options);
      this.gallery.addEventListener('close', () => {
        this.restoreGalleryScroll();
        this.galleryOpener?.focus({ preventScroll: true });
      }, options);
      this.gallery.addEventListener('click', event => {
        if (event.target !== this.gallery) return;
        const bounds = this.gallery.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.gallery.close();
      }, options);
      this.galleryItems.forEach((item, index) => item.addEventListener('click', () => this.setGalleryImage(index), options));
      this.querySelectorAll('[data-gallery-step]').forEach(button => button.addEventListener('click', () => this.setGalleryImage(this.galleryIndex + Number(button.dataset.galleryStep)), options));
      this.gallery.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.setGalleryImage(this.galleryIndex + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
    }
    setGalleryImage(index) {
      if (!this.galleryItems.length) return;
      this.galleryIndex = (index + this.galleryItems.length) % this.galleryItems.length;
      const item = this.galleryItems[this.galleryIndex];
      const image = this.querySelector('[data-gallery-image]');
      image.src = item.dataset.gallerySrc;
      image.alt = item.dataset.galleryAlt;
      this.querySelector('[data-gallery-count]').textContent = `${this.galleryIndex + 1} / ${this.galleryItems.length}`;
      this.querySelector('[data-gallery-caption]').textContent = item.dataset.galleryAlt;
      this.galleryItems.forEach((thumbnail, itemIndex) => thumbnail.setAttribute('aria-pressed', String(itemIndex === this.galleryIndex)));
      if (this.gallery.open) item.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    }
    restoreGalleryScroll() {
      if (this.galleryOverflow === undefined) return;
      document.body.style.overflow = this.galleryOverflow;
      this.galleryOverflow = undefined;
    }
    setupMotion() {
      cancelAnimationFrame(this.motionFrame);
      this.motionFrame = null;
      this.motionController?.abort();
      this.motionController = new AbortController();
      this.revealObserver?.disconnect();
      this.classList.toggle('vp-motion', !this.motionPreference.matches);
      if (this.motionPreference.matches) {
        this.querySelectorAll('[data-reveal]').forEach(element => element.classList.remove('vp-reveal-pending'));
        this.querySelector('[data-tilt]')?.removeAttribute('style');
        return;
      }
      const options = { signal: this.motionController.signal, passive: true };
      this.revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('vp-revealed');
            this.revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12 });
      this.querySelectorAll('[data-reveal]').forEach(element => {
        if (element.getBoundingClientRect().top > innerHeight) element.classList.add('vp-reveal-pending');
        this.revealObserver.observe(element);
      });
      this.motionState = { current: 0, x: 0, y: 0, targetX: 0, targetY: 0 };
      this.stage = this.querySelector('[data-tilt]');
      this.story = this.querySelector('[data-scroll-story]');
      this.storyBeats = [...this.querySelectorAll('[data-story-beat]')];
      this.storyBars = [...this.querySelectorAll('.vp-story-progress span')];
      this.parallaxTargets = [...this.querySelectorAll('[data-parallax]')];
      this.scheduleMotion = () => {
        if (!this.motionFrame && !this.motionPreference.matches) this.motionFrame = requestAnimationFrame(() => this.renderMotion());
      };
      window.addEventListener('scroll', this.scheduleMotion, options);
      window.addEventListener('resize', this.scheduleMotion, options);
      this.stage?.addEventListener('pointermove', event => {
        if (event.pointerType !== 'mouse') return;
        const bounds = this.stage.getBoundingClientRect();
        this.motionState.targetX = (event.clientX - bounds.left) / bounds.width - 0.5;
        this.motionState.targetY = (event.clientY - bounds.top) / bounds.height - 0.5;
        this.scheduleMotion();
      }, options);
      this.stage?.addEventListener('pointerleave', () => {
        this.motionState.targetX = 0;
        this.motionState.targetY = 0;
        this.scheduleMotion();
      }, options);
      this.scheduleMotion();
    }
    renderMotion() {
      this.motionFrame = null;
      if (this.motionPreference.matches || !this.isConnected) return;
      const clamp = value => Math.max(0, Math.min(1, value));
      const state = this.motionState;
      const storyBounds = this.story?.getBoundingClientRect();
      const navHeight = this.querySelector('.vp-subnav').offsetHeight;
      const target = storyBounds ? clamp((navHeight - storyBounds.top) / Math.max(1, storyBounds.height - innerHeight + navHeight)) : 0;
      state.current += (target - state.current) * 0.12;
      state.x += (state.targetX - state.x) * 0.12;
      state.y += (state.targetY - state.y) * 0.12;
      this.story?.style.setProperty('--story-progress', state.current.toFixed(4));
      const active = Math.min(2, Math.floor(state.current * 3));
      this.storyBeats.forEach((beat, index) => {
        beat.classList.toggle('is-story-active', index === active);
        beat.style.setProperty('--beat-opacity', index === active ? '1' : '0');
        beat.style.setProperty('--beat-offset', index === active ? '0px' : '25px');
      });
      this.storyBars.forEach((bar, index) => bar.classList.toggle('is-active', index === active));
      this.parallaxTargets?.forEach(element => {
        const bounds = element.getBoundingClientRect();
        if (bounds.bottom < 0 || bounds.top > innerHeight) return;
        const progress = clamp((innerHeight - bounds.top) / (innerHeight + bounds.height));
        element.style.setProperty('--photo-offset', `${((progress - 0.5) * -28).toFixed(2)}px`);
      });
      this.stage?.style.setProperty('--tilt-x', `${(-state.y * 7).toFixed(2)}deg`);
      this.stage?.style.setProperty('--tilt-y', `${(state.x * 8).toFixed(2)}deg`);
      this.stage?.style.setProperty('--lift', `${(-Math.abs(state.x) * 8).toFixed(2)}px`);
      this.stage?.style.setProperty('--light-x', `${(state.x * 35).toFixed(2)}px`);
      this.stage?.style.setProperty('--light-y', `${(state.y * 35).toFixed(2)}px`);
      if (Math.abs(target - state.current) > 0.0002 || Math.abs(state.targetX - state.x) > 0.001 || Math.abs(state.targetY - state.y) > 0.001) this.scheduleMotion();
    }
    resetButtons() {
      if (!this.busy) return;
      this.busy = false;
      this.form.removeAttribute('aria-busy');
      this.buttons.forEach(button => {
        button.disabled = button.dataset.wasDisabled === 'true';
        button.querySelector('span').textContent = button.dataset.label;
      });
    }
    async addToCart(event) {
      if (event.defaultPrevented || !window.fetch || !window.FormData) return;
      event.preventDefault();
      if (this.busy || !this.form.reportValidity()) return;
      this.busy = true;
      this.form.setAttribute('aria-busy', 'true');
      this.buttons.forEach(button => {
        button.dataset.wasDisabled = String(button.disabled);
        button.dataset.label = button.querySelector('span').textContent;
        button.disabled = true;
        button.querySelector('span').textContent = this.dataset.loading;
      });
      this.status.textContent = this.dataset.loading;
      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form),
          signal: this.abortController.signal
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
        this.status.textContent = this.dataset.added;
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.resetButtons();
        this.status.textContent = error instanceof TypeError ? this.dataset.error : error.message || this.dataset.error;
        this.status.focus({ preventScroll: true });
        this.status.scrollIntoView({ behavior: 'auto', block: 'center' });
      }
    }
  });
}
